<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ProductVariantsAPI from 'dashboard/api/pipelines/productVariants';
import GoogleCategoryPicker from 'dashboard/components/pipelines/GoogleCategoryPicker.vue';

export default {
  components: { NextButton, GoogleCategoryPicker },
  props: {
    selectedProduct: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      name: '',
      description: '',
      productType: '',
      kind: 'physical',
      status: 'active',
      supplierId: null,
      durationMinutes: null,
      tracksInventory: true,
      tags: '',
      googleProductCategoryId: null,
      googleProductCategoryPath: '',
      variants: [],
      images: [],
    };
  },
  validations: {
    name: { required, minLength: minLength(2) },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'products/getUIFlags',
      accountId: 'getCurrentAccountId',
      suppliers: 'suppliers/getSuppliers',
    }),
    modalTitle() {
      return `${this.$t('PRODUCTS.EDIT.TITLE')} - ${this.selectedProduct.name || ''}`;
    },
  },
  mounted() {
    const p = this.selectedProduct;
    this.name = p.name || '';
    this.description = p.description || '';
    this.productType = p.product_type || '';
    this.kind = p.kind || 'physical';
    this.status = p.status || 'active';
    this.supplierId = p.supplier_id || null;
    this.durationMinutes = p.duration_minutes || null;
    this.tracksInventory = p.tracks_inventory !== false;
    this.tags = (p.tags || []).join(', ');
    this.googleProductCategoryId = p.google_product_category_id || null;
    this.googleProductCategoryPath = p.google_product_category_path || '';
    this.variants = (p.variants || []).map(v => ({
      id: v.id,
      name: v.name || '',
      sku: v.sku || '',
      barcode: v.barcode || '',
      price: Number(v.price || 0),
      compare_at_price: v.compare_at_price ? Number(v.compare_at_price) : null,
      cost_price: v.cost_price ? Number(v.cost_price) : null,
      is_default: v.is_default || false,
      stock: v.stock_total || 0,
      _delete: false,
    }));
    if (this.variants.length === 0) {
      this.variants.push({ id: null, name: '', sku: '', barcode: '', price: 0, compare_at_price: null, cost_price: null, is_default: true, stock: 0, _delete: false });
    }
    this.images = (p.images || []).map(img => ({
      id: img.id,
      url: img.url || '',
      alt: img.alt || '',
      _delete: false,
    }));

    this.$store.dispatch('suppliers/get', { accountId: this.accountId });
  },
  methods: {
    onClose() {
      this.$emit('close');
    },

    addVariant() {
      this.variants.push({ id: null, name: '', sku: '', barcode: '', price: 0, compare_at_price: null, cost_price: null, is_default: false, stock: 0, _delete: false });
    },

    removeVariant(idx) {
      const v = this.variants[idx];
      if (v.id) {
        v._delete = true;
      } else {
        this.variants.splice(idx, 1);
      }
      const visible = this.variants.filter(x => !x._delete);
      if (visible.length > 0 && !visible.some(x => x.is_default)) {
        visible[0].is_default = true;
      }
    },

    setDefault(idx) {
      this.variants.forEach((v, i) => { v.is_default = i === idx; });
    },

    addImage() {
      this.images.push({ id: null, url: '', alt: '', _delete: false });
    },

    removeImage(idx) {
      const img = this.images[idx];
      if (img.id) {
        img._delete = true;
      } else {
        this.images.splice(idx, 1);
      }
    },

    parseTags(str) {
      return str ? str.split(',').map(t => t.trim()).filter(Boolean) : [];
    },

    async update() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        const productPayload = {
          name: this.name.trim(),
          description: this.description?.trim() || null,
          product_type: this.productType?.trim() || null,
          kind: this.kind,
          status: this.status,
          supplier_id: this.supplierId || null,
          duration_minutes: this.kind === 'service' ? (Number(this.durationMinutes) || null) : null,
          tracks_inventory: this.tracksInventory,
          tags: this.parseTags(this.tags),
          google_product_category_id: this.googleProductCategoryId || null,
          google_product_category_path: this.googleProductCategoryPath || null,
        };

        await this.$store.dispatch('products/update', {
          accountId: this.accountId,
          id: this.selectedProduct.id,
          product: productPayload,
        });

        const productId = this.selectedProduct.id;

        for (const v of this.variants) {
          if (v._delete && v.id) {
            await this.$store.dispatch('products/deleteVariant', { accountId: this.accountId, productId, id: v.id });
          } else if (!v._delete) {
            const { id, _delete, stock, ...variantData } = v;
            if (id) {
              await this.$store.dispatch('products/updateVariant', { accountId: this.accountId, productId, id, variant: { ...variantData, available: stock } });
            } else {
              await this.$store.dispatch('products/createVariant', { accountId: this.accountId, productId, variant: { ...variantData, available: stock } });
            }
          }
        }

        for (const img of this.images) {
          if (img._delete && img.id) {
            await this.$store.dispatch('products/deleteImage', { accountId: this.accountId, productId, id: img.id });
          } else if (!img._delete && !img.id && img.url?.trim()) {
            await this.$store.dispatch('products/createImage', { accountId: this.accountId, productId, image: { url: img.url.trim(), alt: img.alt?.trim() || null } });
          }
        }

        await this.$store.dispatch('products/get', { accountId: this.accountId });

        useAlert(this.$t('PRODUCTS.EDIT.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.errors?.[0] ||
          this.$t('PRODUCTS.EDIT.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="modalTitle" />
    <form class="flex flex-wrap mx-0 gap-0" @submit.prevent="update">

      <woot-input
        v-model="name"
        class="w-full"
        :class="{ error: v$.name.$error }"
        :label="$t('PRODUCTS.FORM.NAME.LABEL')"
        :placeholder="$t('PRODUCTS.FORM.NAME.PLACEHOLDER')"
        :error="v$.name.$error ? $t('PRODUCTS.FORM.NAME.ERROR') : ''"
      />

      <div class="w-full mb-4 flex gap-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('PRODUCTS.FORM.KIND.LABEL') }}
          </label>
          <select v-model="kind" class="w-full">
            <option value="physical">{{ $t('PRODUCTS.KIND.physical') }}</option>
            <option value="service">{{ $t('PRODUCTS.KIND.service') }}</option>
            <option value="digital">{{ $t('PRODUCTS.KIND.digital') }}</option>
          </select>
        </div>
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('PRODUCTS.FORM.STATUS.LABEL') }}
          </label>
          <select v-model="status" class="w-full">
            <option value="draft">{{ $t('PRODUCTS.STATUS.draft') }}</option>
            <option value="active">{{ $t('PRODUCTS.STATUS.active') }}</option>
            <option value="archived">{{ $t('PRODUCTS.STATUS.archived') }}</option>
            <option value="unlisted">{{ $t('PRODUCTS.STATUS.unlisted') }}</option>
          </select>
        </div>
      </div>

      <woot-input
        v-if="kind === 'service'"
        v-model.number="durationMinutes"
        type="number"
        min="1"
        class="w-full"
        :label="$t('PRODUCTS.FORM.DURATION.LABEL')"
        :placeholder="$t('PRODUCTS.FORM.DURATION.PLACEHOLDER')"
      />

      <div class="w-full mb-4 flex gap-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('PRODUCTS.FORM.SUPPLIER.LABEL') }}
          </label>
          <select v-model="supplierId" class="w-full">
            <option :value="null">{{ $t('PRODUCTS.FORM.SUPPLIER.PLACEHOLDER') }}</option>
            <option v-for="sup in suppliers" :key="sup.id" :value="sup.id">{{ sup.name }}</option>
          </select>
        </div>
      </div>

      <woot-input
        v-model="productType"
        class="w-full"
        label="Tipo de produto"
        placeholder="Ex: Suplemento, Vestuário..."
      />

      <woot-input
        v-model="tags"
        class="w-full"
        :label="$t('PRODUCTS.FORM.TAGS.LABEL')"
        :placeholder="$t('PRODUCTS.FORM.TAGS.PLACEHOLDER')"
      />

      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          Categoria Google
        </label>
        <GoogleCategoryPicker
          v-model="googleProductCategoryId"
          v-model:model-path="googleProductCategoryPath"
          :account-id="accountId"
        />
      </div>

      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ $t('PRODUCTS.FORM.DESCRIPTION.LABEL') }}
        </label>
        <textarea
          v-model="description"
          class="w-full h-24 resize-none"
          :placeholder="$t('PRODUCTS.FORM.DESCRIPTION.PLACEHOLDER')"
        />
      </div>

      <div class="flex items-center w-full gap-2 mb-4">
        <input v-model="tracksInventory" type="checkbox" />
        <label class="text-sm text-n-slate-12">{{ $t('PRODUCTS.FORM.TRACKS_INVENTORY.LABEL') }}</label>
      </div>

      <!-- Variants -->
      <div class="w-full mb-2">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-semibold text-n-slate-12">Variantes</span>
          <NextButton faded slate size="xs" type="button" :label="$t('PRODUCTS.FORM.ADD_VARIANT')" icon="i-lucide-plus" @click.prevent="addVariant" />
        </div>

        <template v-for="(variant, idx) in variants" :key="idx">
          <div v-if="!variant._delete" class="border border-n-slate-6 rounded-lg p-3 mb-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-medium text-n-slate-11">
                {{ variant.is_default ? '★ Padrão' : `Variante ${idx + 1}` }}
                {{ variant.id ? '' : ' (nova)' }}
              </span>
              <div class="flex gap-2">
                <button v-if="!variant.is_default" type="button" class="text-xs text-blue-600 hover:underline" @click="setDefault(idx)">Definir padrão</button>
                <button type="button" class="text-xs text-red-500 hover:underline" @click="removeVariant(idx)">{{ $t('PRODUCTS.FORM.REMOVE_VARIANT') }}</button>
              </div>
            </div>
            <div class="flex gap-3">
              <woot-input v-model="variant.name" class="flex-1" :label="$t('PRODUCTS.VARIANT_FORM.NAME.LABEL')" :placeholder="$t('PRODUCTS.VARIANT_FORM.NAME.PLACEHOLDER')" />
              <woot-input v-model="variant.sku" class="flex-1" :label="$t('PRODUCTS.VARIANT_FORM.SKU.LABEL')" :placeholder="$t('PRODUCTS.VARIANT_FORM.SKU.PLACEHOLDER')" />
            </div>
            <div class="flex gap-3">
              <woot-input v-model.number="variant.price" type="number" step="0.01" min="0" class="flex-1" :label="$t('PRODUCTS.VARIANT_FORM.PRICE.LABEL')" :placeholder="$t('PRODUCTS.VARIANT_FORM.PRICE.PLACEHOLDER')" />
              <woot-input v-model.number="variant.compare_at_price" type="number" step="0.01" min="0" class="flex-1" :label="$t('PRODUCTS.VARIANT_FORM.COMPARE_AT_PRICE.LABEL')" :placeholder="$t('PRODUCTS.VARIANT_FORM.COMPARE_AT_PRICE.PLACEHOLDER')" />
              <woot-input v-if="tracksInventory" v-model.number="variant.stock" type="number" min="0" class="flex-1" :label="$t('PRODUCTS.VARIANT_FORM.STOCK.LABEL')" :placeholder="$t('PRODUCTS.VARIANT_FORM.STOCK.PLACEHOLDER')" />
            </div>
          </div>
        </template>
      </div>

      <!-- Images -->
      <div class="w-full mb-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-semibold text-n-slate-12">Imagens</span>
          <NextButton faded slate size="xs" type="button" :label="$t('PRODUCTS.FORM.ADD_IMAGE')" icon="i-lucide-plus" @click.prevent="addImage" />
        </div>
        <template v-for="(img, idx) in images" :key="idx">
          <div v-if="!img._delete" class="flex gap-3 mb-2 items-end">
            <woot-input v-model="img.url" class="flex-1" :label="$t('PRODUCTS.IMAGE_FORM.URL.LABEL')" :placeholder="$t('PRODUCTS.IMAGE_FORM.URL.PLACEHOLDER')" />
            <woot-input v-model="img.alt" class="flex-1" :label="$t('PRODUCTS.IMAGE_FORM.ALT.LABEL')" :placeholder="$t('PRODUCTS.IMAGE_FORM.ALT.PLACEHOLDER')" />
            <button type="button" class="text-red-500 text-xs mb-4" @click="removeImage(idx)">✕</button>
          </div>
        </template>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton faded slate type="reset" :label="$t('PRODUCTS.FORM.CANCEL')" @click.prevent="onClose" />
        <NextButton type="submit" :label="$t('PRODUCTS.FORM.EDIT')" :disabled="uiFlags.isUpdating" :is-loading="uiFlags.isUpdating" />
      </div>
    </form>
  </div>
</template>
